import type { Contact, PhoneNumber } from '@klareospace/pharos-lib-types';
import { getFullName } from './string';

/** Minimal shape needed to compute a customer's default contact (name + phone). */
interface CustomerContactSource {
  firstName: string;
  lastName: string;
  phone?: PhoneNumber;
}

/**
 * Builds the default `Contact` for a customer: `{ name: "First Last", phone }`.
 *
 * Legacy tolerance: `phone` is accepted as optional here because some customers created
 * before `Customer.phone` became mandatory may still lack it in the database. The `phone`
 * key is omitted entirely at runtime when absent (never set to `undefined`) so callers
 * persisting this object never write a stale `phone: null` field. The cast to `Contact`
 * reflects this intentional, narrowly-scoped deviation from strict typing — reading legacy
 * phone-less customers must never throw.
 *
 * @example buildCustomerDefaultContact({ firstName: 'John', lastName: 'Doe', phone: { dialCode: '+33', number: '612345678' } })
 * // { name: 'John Doe', phone: { dialCode: '+33', number: '612345678' } }
 */
export function buildCustomerDefaultContact(customer: CustomerContactSource): Contact {
  const contact: Partial<Contact> = { name: getFullName(customer.firstName, customer.lastName) };
  if (customer.phone) {
    contact.phone = customer.phone;
  }
  return contact as Contact;
}

/**
 * Compares two contacts for equality on `name` + `phone.dialCode` + `phone.number`.
 * Returns `false` when either side is undefined.
 */
export function isSameContact(a?: Contact, b?: Contact): boolean {
  if (!a || !b) {
    return false;
  }
  return a.name === b.name && a.phone?.dialCode === b.phone?.dialCode && a.phone?.number === b.phone?.number;
}
